/**
 * Re-anchoring for Refine Wording (MDRS-549 / RB-11).
 *
 * `handleRefine` resolves the selection's ProseMirror range, then awaits the
 * refine service for a second or more. PM positions are absolute document
 * offsets, so ANY transaction dispatched while the request is in flight —
 * typing, dictation, the spell-check overlay's click-to-fix — shifts every
 * position after it. Using the captured offsets afterwards wrote the refined
 * prose onto the wrong span, silently, in a medicolegal report.
 *
 * The editor is deliberately NOT locked during the call (that would cut
 * dictation off mid-thought for a round-trip measured in seconds), so the
 * range has to survive concurrent edits instead. This module does that in two
 * steps, both of which run against the doc as it stands when the response
 * lands:
 *
 *  1. **Re-anchor.** The range is remembered as an offset inside the paragraph
 *     identified by its Word `paraId`, not as a bare document offset. Re-finding
 *     that paragraph absorbs every edit made in OTHER paragraphs — which is the
 *     common, recoverable case ("the user typed above the selection").
 *  2. **Verify.** Whatever range comes out is only used if the text sitting
 *     there still matches what was sent to the service (whitespace-normalised).
 *     If it doesn't, the caller makes NO change and says so — the same
 *     fail-safe posture `checkRefineSelection` takes for structural risk.
 *
 * A single-block selection gets one extra attempt before giving up: locate the
 * source text inside the anchor paragraph, accepted only when it occurs exactly
 * once AND the resulting range verifies. That recovers an edit made earlier in
 * the SAME paragraph, and mirrors what `handleOriginal` already does at revert
 * time via `locateUniqueOccurrence` — except that `handleOriginal` maps its
 * character index straight onto a position without verifying, so it carries the
 * inline-leaf defect described at step 3 below. Worth fixing there too; not
 * touched here. Multi-paragraph selections skip the step — there is no single
 * paragraph to search, and a long selection that failed step 1 has genuinely
 * moved.
 *
 * Deliberately NOT a live ProseMirror `Mapping`: the fork owns the EditorView
 * and exposes no transaction hook, so tracking every in-flight transaction
 * would mean reconfiguring its plugins or patching its dispatch. Re-anchoring
 * reaches the same outcome for the cases worth recovering, and the verify step
 * makes the remaining ones fail loudly instead of silently.
 */
import { type Node as PMNode } from "prosemirror-model";
import { type EditorState } from "prosemirror-state";

import {
  locateUniqueOccurrence,
  normalizeForMatch,
} from "../utils/refinement-snapshots";

/** Everything needed to re-find a refine range after the service responds.
 *  Captured BEFORE the await, while the document still matches the selection. */
export type RefineAnchor = {
  /** Word `w14:paraId` of the paragraph the range starts in; null when the
   *  editor couldn't report one (we then only verify the original offsets). */
  paraId: string | null;
  /** Offset of `from` inside the anchor paragraph's content, or null when the
   *  range starts outside it (the paragraph couldn't be used as an anchor). */
  startOffset: number | null;
  /** The anchor paragraph's text as it was when the range was captured. Lets
   *  the rebase tell "this paragraph is untouched" from "this paragraph was
   *  edited and the text at my offset merely LOOKS right". */
  paragraphText: string | null;
  /** The document as it was at capture time. PM docs are immutable, so
   *  identity equality is an exact "not one transaction has landed" test —
   *  and that is the ONLY thing that justifies reusing a raw offset. */
  doc: PMNode;
  /** The range as captured, used when nothing shifted. */
  from: number;
  to: number;
  /** The exact text sent to the refine service. */
  sourceText: string;
  /** Whether the range sits inside a single textblock. */
  singleBlock: boolean;
};

export type RefineRebase =
  | { ok: true; from: number; to: number }
  | { ok: false; reason: "missing" | "changed" };

/**
 * Position of the paragraph carrying `paraId` — but ONLY when exactly one
 * paragraph carries it.
 *
 * An in-editor copy/paste of a paragraph brings its Word `paraId` along, so a
 * document can hold twins. A first-match lookup would happily re-anchor onto
 * the wrong one and, because the twins' text is identical, the verify step
 * would wave it through. Ambiguity here means "don't use the paragraph
 * anchor", the same way ambiguous text means "don't relocate".
 */
const locateAnchorParagraph = (
  doc: PMNode,
  paraId: string
): { pos: number | null; duplicated: boolean } => {
  let pos: number | null = null;
  let count = 0;
  doc.descendants((node, nodePos) => {
    if (count > 1) return false;
    if (
      (node.type.name === "paragraph" || node.type.name === "doc_paragraph") &&
      node.attrs?.paraId === paraId
    ) {
      count += 1;
      if (pos === null) pos = nodePos;
      return false;
    }
    return true;
  });
  return { pos: count === 1 ? pos : null, duplicated: count > 1 };
};

/** Text of `[from, to]` in the same form `handleRefine` sends to the service
 *  (block boundary → blank line, hardBreak → newline). */
const rangeText = (doc: PMNode, from: number, to: number): string => {
  if (from < 0 || to > doc.content.size || from >= to) return "";
  return doc.textBetween(from, to, "\n\n", "\n");
};

/** Does the text currently at `[from, to]` still match what we refined?
 *  Whitespace-normalised, so a trimmed `sourceText` still matches a range that
 *  includes the surrounding spaces. The flip side: a candidate that differs
 *  from the real range by whitespace alone verifies as a match, and the replace
 *  then eats that whitespace. Accepted — refusing on a stray space would make
 *  refine feel broken, and the worst case is a space, not misplaced prose. */
const matchesSource = (
  doc: PMNode,
  from: number,
  to: number,
  sourceText: string
): boolean => {
  const current = normalizeForMatch(rangeText(doc, from, to));
  return current.length > 0 && current === normalizeForMatch(sourceText);
};

/**
 * Snapshot the range so it can be re-found after the await. Call this BEFORE
 * dispatching anything or awaiting — `paraPos` must be the anchor paragraph's
 * position in the same state `from`/`to` were resolved against.
 */
export const captureRefineAnchor = (
  state: EditorState,
  args: {
    paraId: string | null;
    paraPos: number;
    from: number;
    to: number;
    sourceText: string;
  }
): RefineAnchor => {
  const { paraId, paraPos, from, to, sourceText } = args;
  // `from` normally sits inside the anchor paragraph's content, which starts
  // one position after its open token. A negative offset means the resolved
  // range started before that paragraph (a selection whose reported paraId
  // isn't its first block) — unusable as an anchor, so fall back to verifying
  // the original offsets.
  const startOffset = from - (paraPos + 1);
  let singleBlock = true;
  try {
    singleBlock = state.doc.resolve(from).sameParent(state.doc.resolve(to));
  } catch {
    singleBlock = false;
  }
  return {
    paraId,
    startOffset: startOffset >= 0 ? startOffset : null,
    paragraphText: state.doc.nodeAt(paraPos)?.textContent ?? null,
    doc: state.doc,
    from,
    to,
    sourceText,
    singleBlock,
  };
};

/**
 * Re-find the anchored range in `state`'s current document.
 *
 * Returns the rebased `[from, to]` only when the text there still matches what
 * was refined. `changed` means the selected text itself was edited during the
 * call; `missing` means its paragraph is gone entirely. Either way the caller
 * must leave the document untouched.
 */
export const rebaseRefineRange = (
  state: EditorState,
  anchor: RefineAnchor
): RefineRebase => {
  const { doc } = state;
  const length = anchor.to - anchor.from;

  const anchorPara = anchor.paraId
    ? locateAnchorParagraph(doc, anchor.paraId)
    : { pos: null, duplicated: false };
  const paraPos = anchorPara.pos;
  const paraNode = paraPos === null ? null : doc.nodeAt(paraPos);

  // Matching on text alone is not enough once the anchor paragraph itself has
  // been edited: inserting a copy of the selected text immediately BEFORE the
  // selection leaves a lookalike sitting at the saved offset, and we would
  // refine the copy while the user's actual selection went untouched. So a
  // saved offset is only trusted when the paragraph is untouched, or when the
  // source text still occurs exactly once in it (nothing to confuse it with).
  const paragraphUnchanged =
    paraNode !== null &&
    anchor.paragraphText !== null &&
    paraNode.textContent === anchor.paragraphText;
  const sourceIsUnique =
    paraNode !== null &&
    locateUniqueOccurrence(paraNode.textContent, anchor.sourceText) >= 0;

  // 1. Re-anchored against the paragraph — absorbs edits made anywhere else in
  //    the document. Tried first: after an edit above, the ORIGINAL offsets can
  //    still coincidentally hold matching text, and the re-anchored range is
  //    the one the user actually selected.
  if (paraPos !== null && anchor.startOffset !== null) {
    const from = paraPos + 1 + anchor.startOffset;
    const to = from + length;
    if (
      (paragraphUnchanged || sourceIsUnique) &&
      matchesSource(doc, from, to, anchor.sourceText)
    ) {
      return { ok: true, from, to };
    }
  }

  // 2. Nothing shifted at all. These are RAW offsets, so the only thing that
  //    justifies them is whole-document identity — PM docs are immutable, so
  //    `doc === anchor.doc` means not one transaction landed.
  //
  //    Deliberately not weakened to "the anchor paragraph is unchanged": an
  //    untouched paragraph says nothing about where it now SITS. Paragraphs
  //    inserted ahead of it can come to occupy the captured offsets with
  //    identical text, and this path would hand back the copies. Nor to "no
  //    paragraph was located" — a `paraId` that turned ambiguous mid-call
  //    lands there. Both were real holes; see the regression tests.
  if (
    doc === anchor.doc &&
    matchesSource(doc, anchor.from, anchor.to, anchor.sourceText)
  ) {
    return { ok: true, from: anchor.from, to: anchor.to };
  }

  // 3. Single-block selections only: the paragraph itself was edited before the
  //    selection. Re-locate the source text inside it, but only if it occurs
  //    exactly once — otherwise we'd be guessing which copy the user meant.
  //
  //    `locateUniqueOccurrence` works in CHARACTERS of `textContent`, which is
  //    not the same axis as PM positions: an inline leaf (hardBreak, tab,
  //    drawing, shape) occupies a position but contributes no character, so the
  //    index is short by one per leaf before the match. Rather than try to
  //    convert the axes, verify the candidate like every other path — a
  //    mis-mapped range simply fails to match and we refuse.
  if (anchor.singleBlock && paraPos !== null) {
    const node = doc.nodeAt(paraPos);
    if (node) {
      const idx = locateUniqueOccurrence(node.textContent, anchor.sourceText);
      if (idx >= 0) {
        const from = paraPos + 1 + idx;
        const to = from + anchor.sourceText.length;
        if (matchesSource(doc, from, to, anchor.sourceText)) {
          return { ok: true, from, to };
        }
      }
    }
  }

  if (!anchorPara.duplicated && paraPos === null && anchor.paraId) {
    return { ok: false, reason: "missing" };
  }
  return { ok: false, reason: "changed" };
};

/** User-facing message when the range couldn't be re-found. Both cases say the
 *  same thing operationally — nothing was replaced, re-select and retry. */
export const refineRangeMessage = (reason: "missing" | "changed"): string =>
  reason === "missing"
    ? "Your selection changed while the wording was being refined — the text is no longer there, so nothing was replaced. Select it again and retry."
    : "Your selection changed while the wording was being refined, so nothing was replaced. Select the text again and retry.";
